import { useState } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { getApiBaseUrl } from '../constants';

interface SetupWizardProps {
  userProfile: { name: string; email: string };
  onComplete: () => void;
}

interface ClassSetup {
  period: number;
  subject: string;
  teacher: string;
  enabled: boolean;
}

type Step = 'role' | 'name' | 'grade' | 'periods' | 'classes' | 'saving';

const darkInput: React.CSSProperties = {
  width: '100%', padding: '0.75rem', fontSize: '1.1rem', borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.1)', boxSizing: 'border-box',
  background: 'rgba(255,255,255,0.07)', color: '#f0f0f5', fontFamily: 'inherit',
};

const cardBtn: React.CSSProperties = {
  flex: 1, padding: '2rem 1rem', fontSize: '1.1rem', borderRadius: 16,
  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
  cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center',
  gap: '0.5rem', color: '#f0f0f5', backdropFilter: 'blur(12px)', fontFamily: 'inherit',
  transition: 'all 0.2s ease',
};

function SetupWizard({ userProfile, onComplete }: SetupWizardProps) {
  const { getToken } = useAuth();
  const { user: clerkUser } = useUser();

  const [step, setStep] = useState<Step>('role');
  const [role, setRole] = useState<'student' | 'parent'>('student');
  const [name, setName] = useState(clerkUser?.firstName || userProfile.name || '');
  const [grade, setGrade] = useState<number>(6);
  const [periodCount, setPeriodCount] = useState<number>(7);
  const [classes, setClasses] = useState<ClassSetup[]>([]);
  const [error, setError] = useState('');

  const initClasses = (count: number) => {
    const newClasses: ClassSetup[] = [];
    for (let i = 1; i <= count; i++) {
      newClasses.push({ period: i, subject: '', teacher: '', enabled: true });
    }
    setClasses(newClasses);
  };

  const updateClass = (index: number, field: keyof ClassSetup, value: string | boolean) => {
    setClasses(prev => prev.map((cls, i) =>
      i === index ? { ...cls, [field]: value } : cls
    ));
  };

  const handleSubmit = async () => {
    setStep('saving');
    setError('');
    try {
      const token = await getToken();
      const body: any = { name, role };
      if (role === 'student') {
        body.grade = grade;
        body.school = null;
        body.classes = classes;
      }
      const res = await fetch(`${getApiBaseUrl()}/me/setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Setup failed');
      }
      onComplete();
    } catch (err: any) {
      setError(err.message || 'Setup failed. Please try again.');
      setStep(role === 'parent' ? 'name' : 'classes');
    }
  };

  const backBtnStyle: React.CSSProperties = { flex: 1, padding: '0.75rem', fontSize: '1rem', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', color: '#8b8b9e', fontFamily: 'inherit' };
  const nextBtnStyle: React.CSSProperties = { flex: 2, padding: '0.75rem', fontSize: '1.1rem', borderRadius: 8, background: 'linear-gradient(135deg, #7c3aed, #f59e0b)', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' };

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '2rem 1rem', minHeight: '100vh', background: '#0a0a0f' }}>
      <h1 style={{ textAlign: 'center', marginBottom: '0.5rem', background: 'linear-gradient(135deg, #a78bfa, #f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>📚 Welcome to Homework Tracker!</h1>
      <p style={{ textAlign: 'center', color: '#8b8b9e', marginBottom: '2rem' }}>Let's get you set up.</p>

      {error && (
        <div className="banner banner--error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {step === 'role' && (
        <div>
          <h2 style={{ color: '#f0f0f5' }}>Are you a student or a parent?</h2>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button onClick={() => { setRole('student'); setStep('name'); }} style={cardBtn}>
              <span style={{ fontSize: '2.5rem' }}>🎒</span>
              <span style={{ fontWeight: 600 }}>Student</span>
              <span style={{ fontSize: '0.85rem', color: '#8b8b9e' }}>Track my homework</span>
            </button>
            <button onClick={() => { setRole('parent'); setStep('name'); }} style={cardBtn}>
              <span style={{ fontSize: '2.5rem' }}>👨‍👩‍👧</span>
              <span style={{ fontWeight: 600 }}>Parent</span>
              <span style={{ fontSize: '0.85rem', color: '#8b8b9e' }}>View my child's homework</span>
            </button>
          </div>
        </div>
      )}

      {step === 'name' && (
        <div>
          <h2 style={{ color: '#f0f0f5' }}>What's your name?</h2>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Your name"
            style={darkInput}
            autoFocus
          />
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button onClick={() => setStep('role')} style={backBtnStyle}>← Back</button>
            <button
              onClick={() => {
                if (!name.trim()) return;
                if (role === 'parent') { handleSubmit(); } else { setStep('grade'); }
              }}
              disabled={!name.trim()}
              style={{ ...nextBtnStyle, opacity: !name.trim() ? 0.5 : 1 }}
            >
              {role === 'parent' ? 'Finish Setup ✓' : 'Next →'}
            </button>
          </div>
        </div>
      )}

      {step === 'grade' && (
        <div>
          <h2 style={{ color: '#f0f0f5' }}>What grade are you in?</h2>
          <select value={grade} onChange={e => setGrade(parseInt(e.target.value))} style={darkInput}>
            {[5, 6, 7, 8, 9, 10, 11, 12].map(g => (
              <option key={g} value={g}>{g}th Grade</option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button onClick={() => setStep('name')} style={backBtnStyle}>← Back</button>
            <button onClick={() => setStep('periods')} style={nextBtnStyle}>Next →</button>
          </div>
        </div>
      )}

      {step === 'periods' && (
        <div>
          <h2 style={{ color: '#f0f0f5' }}>How many class periods do you have?</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
            <input type="range" min={4} max={8} value={periodCount} onChange={e => setPeriodCount(parseInt(e.target.value))} style={{ flex: 1, accentColor: '#7c3aed' }} />
            <span style={{ fontSize: '1.5rem', fontWeight: 'bold', minWidth: 30, textAlign: 'center', color: '#a78bfa' }}>{periodCount}</span>
          </div>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button onClick={() => setStep('grade')} style={backBtnStyle}>← Back</button>
            <button onClick={() => { initClasses(periodCount); setStep('classes'); }} style={nextBtnStyle}>Next →</button>
          </div>
        </div>
      )}

      {step === 'classes' && (
        <div>
          <h2 style={{ color: '#f0f0f5' }}>Set up your classes</h2>
          <p style={{ color: '#8b8b9e', marginBottom: '1rem' }}>Enter the subject and teacher for each period.</p>
          {classes.map((cls, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', minWidth: 80 }}>
                <input type="checkbox" checked={cls.enabled} onChange={e => updateClass(i, 'enabled', e.target.checked)} style={{ accentColor: '#7c3aed' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#a78bfa' }}>P{cls.period}</span>
              </label>
              <input
                type="text" value={cls.subject} onChange={e => updateClass(i, 'subject', e.target.value)}
                placeholder="Subject" disabled={!cls.enabled}
                style={{ ...darkInput, flex: 1, width: 'auto', opacity: cls.enabled ? 1 : 0.4 }}
              />
              <input
                type="text" value={cls.teacher} onChange={e => updateClass(i, 'teacher', e.target.value)}
                placeholder="Teacher" disabled={!cls.enabled}
                style={{ ...darkInput, flex: 1, width: 'auto', opacity: cls.enabled ? 1 : 0.4 }}
              />
            </div>
          ))}
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button onClick={() => setStep('periods')} style={backBtnStyle}>← Back</button>
            <button
              onClick={handleSubmit}
              disabled={classes.filter(c => c.enabled).some(c => !c.subject.trim())}
              style={{ ...nextBtnStyle, opacity: classes.filter(c => c.enabled).some(c => !c.subject.trim()) ? 0.5 : 1 }}
            >
              Finish Setup ✓
            </button>
          </div>
        </div>
      )}

      {step === 'saving' && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ width: 40, height: 40, border: '3px solid rgba(124,58,237,0.2)', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ fontSize: '1.2rem', color: '#8b8b9e' }}>Setting up your account...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
    </div>
  );
}

export default SetupWizard;
